import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  Check as CheckIcon,
  Block as BlockIcon,
  SwapHoriz as SwapIcon,
  AutoMode as AutoIcon,
} from '@mui/icons-material';
import { OVERRIDE_DECISIONS, DECISION_LABELS } from './hooks/useOverrides';

/**
 * Badge showing override status on a recommendation.
 */
export default function OverrideBadge({ override, size = 'small' }) {
  if (!override) return null;

  const { decision, note } = override;

  const getChipProps = () => {
    switch (decision) {
      case OVERRIDE_DECISIONS.FORCE_COMPLEMENT:
        return {
          icon: <SwapIcon />,
          color: 'success',
          label: 'Complement',
        };
      case OVERRIDE_DECISIONS.FORCE_ADDON:
        return {
          icon: <SwapIcon />,
          color: 'info',
          label: 'Add-on',
        };
      case OVERRIDE_DECISIONS.FORCE_COMPETITOR:
        return {
          icon: <SwapIcon />,
          color: 'error',
          label: 'Competitor',
        };
      case OVERRIDE_DECISIONS.EXCLUDE:
        return {
          icon: <BlockIcon />,
          color: 'default',
          label: 'Excluded',
        };
      case OVERRIDE_DECISIONS.APPROVE:
        return {
          icon: <CheckIcon />,
          color: 'success',
          label: 'Approved',
        };
      case OVERRIDE_DECISIONS.AUTO:
        return {
          icon: <AutoIcon />,
          color: 'default',
          label: 'Auto',
        };
      default:
        return {
          color: 'default',
          label: DECISION_LABELS[decision] || decision,
        };
    }
  };

  const chipProps = getChipProps();
  const tooltipContent = note
    ? `${DECISION_LABELS[decision] || decision}: ${note}`
    : DECISION_LABELS[decision] || decision;

  return (
    <Tooltip title={tooltipContent} arrow>
      <Chip
        size={size}
        variant="outlined"
        {...chipProps}
        sx={{
          fontWeight: 500,
          '& .MuiChip-icon': {
            fontSize: size === 'small' ? 14 : 18,
          },
        }}
      />
    </Tooltip>
  );
}

/**
 * Compact override indicator (just an icon).
 */
export function OverrideIcon({ override, size = 'small' }) {
  if (!override) return null;

  const { decision } = override;
  const iconSize = size === 'small' ? 16 : 20;

  const getIconAndColor = () => {
    switch (decision) {
      case OVERRIDE_DECISIONS.FORCE_COMPLEMENT:
      case OVERRIDE_DECISIONS.FORCE_ADDON:
      case OVERRIDE_DECISIONS.FORCE_COMPETITOR:
        return { Icon: SwapIcon, color: 'primary.main' };
      case OVERRIDE_DECISIONS.EXCLUDE:
        return { Icon: BlockIcon, color: 'text.disabled' };
      case OVERRIDE_DECISIONS.APPROVE:
        return { Icon: CheckIcon, color: 'success.main' };
      default:
        return { Icon: AutoIcon, color: 'text.secondary' };
    }
  };

  const { Icon, color } = getIconAndColor();

  return (
    <Tooltip title={DECISION_LABELS[decision] || decision} arrow>
      <Icon sx={{ fontSize: iconSize, color }} />
    </Tooltip>
  );
}
